// Angular
import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Params } from '@angular/router';

// Libraries
import { Observable, takeUntil } from 'rxjs';
import { Select } from '@ngxs/store';

// Service
import { DynamicSideNavService } from './services/dynamic-side-nav.service';
import { JobService } from '../../../modules/job/services/job.service';

// Models
import { AppCleanup } from '../../models';
import { MenuBadgeModel } from '../../config/menu.config';
import { Job, JobUpdate } from '../../../modules/job/models';
import { HorizontalItem } from '../../models/horizontal-item.model';

// Enums
import { JobType, StatusType, UnitType } from '../../enums';

// Store
import { StorageState } from '../../state/storage.state';

@Component({
	selector: 'dynamic-side-nav',
	templateUrl: './dynamic-side-nav.component.html',
	styleUrl: './dynamic-side-nav.component.scss',
})
export class DynamicSideNavComponent extends AppCleanup implements OnInit, OnDestroy {
	@Select(StorageState.currentRoute) currentRoute$: Observable<string>;
	@Select(StorageState.jobRouteParams) jobRouteParams$: Observable<Params>;
	@Input() menuList: HorizontalItem[];
	jobId: number;
	job: Job;
	opened: boolean = false;
	accordionState: number = -1;
	currentRoute = '';
	pendingUserCount: number;
	disableForceMonitor: boolean; // Disable Force Monitor if Depth and Weight are missing

	constructor(
		private dynamicSideNavService: DynamicSideNavService,
		private jobService: JobService
	) {
		super();
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize() {
		if (window.innerWidth > 1200 && this.opened) {
			this.close();
		}
	}

	ngOnInit() {
		this.currentRoute$.pipe(takeUntil(this.destroy)).subscribe((currentRoute) => {
			this.currentRoute = currentRoute;
		});

		this.dynamicSideNavService.opened.pipe(takeUntil(this.destroy)).subscribe((opened) => {
			this.opened = opened;
		});

		this.jobRouteParams$.pipe(takeUntil(this.destroy)).subscribe((params) => {
			if (params) {
				this.jobId = Number(params.jobId);

				this.jobService
					.getJobStream(String(this.jobId))
					.pipe(takeUntil(this.destroy))
					.subscribe((jobUpdate: JobUpdate) => {
						if (jobUpdate) {
							this.job = jobUpdate.job;

							const unitPeriodsWithDepth: { unitPeriodId: number; unitTypeId: number }[] = this.job.unitPeriodChannels
								.filter((upc) => upc.channel.label.split(' ').includes('Depth'))
								.map((upc) => ({ unitPeriodId: upc.unitPeriodId, unitTypeId: upc.unit.unitTypeId }));
							const unitPeriodsWithWeight: { unitPeriodId: number; unitTypeId: number }[] = this.job.unitPeriodChannels
								.filter((upc) => upc.channel.label.split(' ').includes('Weight'))
								.map((upc) => ({ unitPeriodId: upc.unitPeriodId, unitTypeId: upc.unit.unitTypeId }));
							const unitPeriodsWithDepthAndWeight = unitPeriodsWithDepth.filter((up) => unitPeriodsWithWeight.some((up2) => up2.unitPeriodId == up.unitPeriodId));
							const unitPeriodsWithDepthAndWeightAndCoiledTubing = unitPeriodsWithDepthAndWeight.filter((up) => up.unitTypeId == UnitType.CoiledTubing);
							let forceMonitorUnitPeriodId =
								unitPeriodsWithDepthAndWeightAndCoiledTubing.length > 0
									? unitPeriodsWithDepthAndWeightAndCoiledTubing[0].unitPeriodId
									: unitPeriodsWithDepthAndWeight.length > 0
										? unitPeriodsWithDepthAndWeight[0].unitPeriodId
										: undefined;

							if (this.job.jobType.id !== UnitType.CoiledTubing || !forceMonitorUnitPeriodId) {
								this.disableForceMonitor = true;
							} else {
								this.disableForceMonitor = false;
							}
						}
					});

				// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => {
				// 	if (!this.job && jobChange && jobChange.job?.key == String(this.jobId)) {
				// 		this.job = jobChange.job;

				// 		const unitPeriodsWithDepth: { unitPeriodId: number; unitTypeId: number }[] = this.job.unitPeriodChannels
				// 			.filter((upc) => upc.channel.label.split(' ').includes('Depth'))
				// 			.map((upc) => ({ unitPeriodId: upc.unitPeriodId, unitTypeId: upc.unit.unitTypeId }));
				// 		const unitPeriodsWithWeight: { unitPeriodId: number; unitTypeId: number }[] = this.job.unitPeriodChannels
				// 			.filter((upc) => upc.channel.label.split(' ').includes('Weight'))
				// 			.map((upc) => ({ unitPeriodId: upc.unitPeriodId, unitTypeId: upc.unit.unitTypeId }));
				// 		const unitPeriodsWithDepthAndWeight = unitPeriodsWithDepth.filter((up) => unitPeriodsWithWeight.some((up2) => up2.unitPeriodId == up.unitPeriodId));
				// 		const unitPeriodsWithDepthAndWeightAndCoiledTubing = unitPeriodsWithDepthAndWeight.filter((up) => up.unitTypeId == UnitType.CoiledTubing);
				// 		let forceMonitorUnitPeriodId =
				// 			unitPeriodsWithDepthAndWeightAndCoiledTubing.length > 0
				// 				? unitPeriodsWithDepthAndWeightAndCoiledTubing[0].unitPeriodId
				// 				: unitPeriodsWithDepthAndWeight.length > 0
				// 					? unitPeriodsWithDepthAndWeight[0].unitPeriodId
				// 					: undefined;

				// 		if (this.job.jobType.id !== UnitType.CoiledTubing || !forceMonitorUnitPeriodId) {
				// 			this.disableForceMonitor = true;
				// 		} else {
				// 			this.disableForceMonitor = false;
				// 		}
				// 	}
				// });
			} else {
				this.jobId = null;
			}
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	close() {
		this.dynamicSideNavService.opened.next(false);
	}

	collapseAccordions() {
		this.accordionState = -1; // Collapses accordion menu
	}

	setAccordionState(state: number) {
		this.accordionState = state;
	}

	getItemClass(item) {
		let classes = '';

		if (item.bottom) {
			classes += ' mt-auto';
		}

		if (item.root && (this.isItemActive(item) || this.isChildItemActive(item))) {
			classes += ' active';
		}

		if (!item.root && this.isItemActive(item)) {
			classes += ' active';
		}

		if (this.isDisabled(item)) {
			classes += ' disabled';
		}

		return classes;
	}

	isDisabled(item: HorizontalItem) {
		if (item.title === 'Force Monitor') {
			if (this.disableForceMonitor) {
				return true;
			}
		}

		return false;
	}

	disableTooltip(item: HorizontalItem) {
		if (item.title === 'Force Monitor') {
			if (this.disableForceMonitor) {
				return false;
			}
		}

		return true;
	}

	getTooltip(item: HorizontalItem) {
		if (item.title === 'Force Monitor') {
			if (this.disableForceMonitor) {
				if (this.job.jobType.id !== JobType.CoiledTubing) {
					return "Force Monitor is only available for job type 'Coiled Tubing'";
				} else {
					return "Can't use Force Monitor without depth and weight channels";
				}
			}
		}

		return '';
	}

	isItemActive(item): boolean {
		if (!item.page) {
			return false;
		}

		return this.currentRoute === item.page || this.currentRoute.includes(item.page);
	}

	isChildItemActive(item): boolean {
		let result = false;

		if (item.submenu && item.submenu.length > 0) {
			for (const subItem of item.submenu) {
				if (this.currentRoute === subItem.page) {
					result = true;
				}
			}
		}

		return result;
	}

	getBadgeClass(badge: MenuBadgeModel) {
		let elementClass: string;

		if (badge?.type === StatusType.SUCCESS) {
			elementClass = 'success';
		} else if (badge?.type === StatusType.DANGER) {
			elementClass = 'danger';
		} else if (badge?.type === StatusType.WARNING) {
			elementClass = 'warning';
		}

		return elementClass;
	}
}
